import {
  api,
  protectedDeleteWithBody,
  protectedGet,
  protectedPatch,
  protectedPost,
} from "./restUtil";
import { transformPlan } from "./planUtil";
import * as _ from "underscore";
import moment from "moment";
import { SessionStatus } from "./sessionStatus";
import { getCurrentServerTime, getUserFullName } from "./util";
import { getSessionUser, isUserTeacher } from "./authUtil";
import { fetchStudentProfile } from "./students";

export const CHAT_MAX_SIZE_REACHED_ERROR = "Chat growing too large";
const ASSESSMENT_SESSION_TYPE = "assessment-session";

export function fetchUpcomingSessions(args, onSuccess, onErrors) {
  return protectedGet(
    "/sessions/lesson/upcoming",
    undefined,
    resp => {
      if (!resp || !resp.list) {
        return onErrors && onErrors("invalid response structure");
      } else {
        return onSuccess && onSuccess(resp.list);
      }
    },
    onErrors
  );
}

export function fetchCompletedSessions(args, onSuccess, onErrors) {
  return protectedGet(
    "/sessions/lesson/completed",
    {
      page: args.page,
      size: args.size,
    },
    resp => {
      if (!resp || !resp.list) {
        return onErrors && onErrors("invalid response structure");
      } else {
        return onSuccess && onSuccess(resp);
      }
    },
    onErrors
  );
}

export function fetchCompletedMore(args, onSuccess, onErrors) {
  return fetchCompletedSessions(args, onSuccess, onErrors);
}

export function fetchSessionsForMonitoring(args, onSuccess, onError) {
  let queryStr = "from=" + args.from + "&to=" + args.to;
  return protectedGet(
    "/sessions/lesson/monitoring?" + queryStr,
    undefined,
    resp => {
      return onSuccess && onSuccess(resp.list);
    },
    onError
  );
}

export function searchSessions(args, onSuccess, onErrors) {
  return protectedGet(
    "/sessions/lesson",
    {
      contains: encodeURIComponent(args?.search || ""),
      filter: "status;" + args.statuses.join(","),
      page: args.page,
      size: args.size,
      order: args.order || undefined,
    },
    resp => {
      if (!resp || !resp.list) {
        return onErrors && onErrors("invalid response structure");
      } else {
        return onSuccess && onSuccess(resp);
      }
    },
    onErrors
  );
}

export function searchMore(args, onSuccess, onErrors) {
  return searchSessions(args, onSuccess, onErrors);
}

export function fetchFeedbackRequired(args, callback) {
  return fetch("", callback);
}

export function fetchTeacherCalendar(args, onSuccess, onErrors) {
  let sessions_resp;
  let aggregated;
  const fetchSessions = new Promise((resolve, reject) => {
    return protectedGet(
      "/sessions?from=" + args.from + "&to=" + args.to,
      undefined,
      resp => {
        sessions_resp = resp;
        return resolve(resp);
      },
      reject
    );
  });

  const fetchEffective = new Promise((resolve, reject) => {
    return protectedGet(
      "/sessions/aggregated?from=" + args.from + "&to=" + args.to,
      undefined,
      resp => {
        aggregated = resp;
        return resolve(resp);
      },
      reject
    );
  });

  Promise.all([fetchSessions, fetchEffective]).then(() => {
    sessions_resp.aggregated = aggregated ? aggregated.list : [];
    return onSuccess({
      args: args,
      resp: sessions_resp,
    });
  }, onErrors);
}

export function fetchStudentCalendar(args, onSuccess, onErrors) {
  let sessions_resp;
  let aggregated;
  const fetchSessions = new Promise((resolve, reject) => {
    return protectedGet(
      "/sessions?from=" + args.from + "&to=" + args.to,
      undefined,
      resp => {
        sessions_resp = resp;
        _.each(sessions_resp.sessions, s => {
          delete s.instructor.last_name;
        });
        return resolve(resp);
      },
      reject
    );
  });

  const fetchEffective = new Promise((resolve, reject) => {
    return protectedGet(
      "/instructor/" +
        args.instructor_id +
        "/sessions/aggregated?" +
        "from=" +
        args.from +
        "&to=" +
        args.to,
      undefined,
      resp => {
        aggregated = resp;
        return resolve(resp);
      },
      reject
    );
  });

  Promise.all([fetchSessions, fetchEffective]).then(() => {
    sessions_resp.aggregated = aggregated ? aggregated.list : [];
    return onSuccess({
      args: args,
      resp: sessions_resp,
    });
  }, onErrors);
}

export function fetchDefaultMask() {
  return api.get("/sessions", {
    from: moment().subtract(1, "day").toISOString(),
    to: moment().add(1, "day").toISOString(),
  });
}

export function checkAvailableArgs(args) {
  if (_.isEmpty(args)) {
    return;
  }
  let _args = {};
  if (args.recur) {
    _args.recur = args.recur;
  }
  if (args.available && args.available.length > 0) {
    _args.available = args.available;
  }
  if (_.isEmpty(_args)) {
    return;
  }

  return _args;
}

export function checkBusyArgs(args) {
  if (_.isEmpty(args)) {
    return;
  }
  let _args = {};
  // TODO delete recur?
  if (args.recur) {
    _args.recur = args.recur;
  }
  if (args.busy && args.busy.length > 0) {
    _args.busy = args.busy;
  }
  if (_.isEmpty(_args)) {
    return;
  }

  return _args;
}

export function addAvailable(args) {
  const _args = checkAvailableArgs(args);
  if (_args === undefined) {
    return Promise.resolve();
  }

  return api.post("/sessions/available", _args);
}

export function deleteAvailable(args) {
  const _args = checkAvailableArgs(args);
  if (_args === undefined) {
    return Promise.resolve();
  }
  return api.delete("/sessions/available", _args);
}

export function addBusy(args, onSuccess, onErrors) {
  const _args = checkBusyArgs(args);
  if (_args === undefined) {
    return onSuccess && onSuccess();
  }
  return protectedPost("/sessions/busy", _args, onSuccess, onErrors);
}

export function deleteBusy(args, onSuccess, onErrors) {
  const _args = checkBusyArgs(args);
  if (_args === undefined) {
    return onSuccess && onSuccess();
  }
  return protectedDeleteWithBody("/sessions/busy", _args, onSuccess, onErrors);
}

export function fetchSessionsForMonthCommon(
  args,
  onSuccess,
  onErrors,
  endpoint,
  respAttrName
) {
  if (
    args === undefined ||
    args === null ||
    args.month === undefined ||
    args.month === null
  ) {
    return onErrors && onErrors("month is required");
  }
  const from = moment(args.month).utc().startOf("month");
  let to = moment(args.month).utc().endOf("month");

  if (moment().isBefore(moment(to))) {
    to = moment().utc();
  }

  return protectedGet(
    endpoint + "from=" + from.toISOString() + "&to=" + to.toISOString(),
    undefined,
    function (resp) {
      if (!resp || !resp[respAttrName]) {
        return onErrors && onErrors("invalid response structure");
      }

      const possibleStatuses = [
        SessionStatus.COMPLETED,
        SessionStatus.INSTRUCTOR_CANCELED,
        SessionStatus.STUDENT_CANCELED,
        SessionStatus.INSTRUCTOR_MISSED,
        SessionStatus.STUDENT_MISSED,
        SessionStatus.ISSUES_RESCHEDULED,
      ];

      const sessions = _.sortBy(
        _.filter(resp[respAttrName], s => possibleStatuses.indexOf(s.status) > -1),
        s => -moment(s.scheduled).toDate().getTime()
      );
      return onSuccess(sessions);
    },
    onErrors
  );
}

export function fetchSessionsForMonth(args, onSuccess, onErrors) {
  fetchSessionsForMonthCommon(args, onSuccess, onErrors, "/sessions/lesson?", "list");
}

export function createSessionByParticipant(args, onSuccess, onError) {
  return protectedPost("/sessions/lesson", args, onSuccess, onError);
}

export function updateSessionByParticipant(args, onSuccess, onError) {
  return protectedPatch(
    "/session/lesson/" + args.session_id,
    args.session,
    onSuccess,
    onError
  );
}

export function changeSession(args, onSuccess, onError) {
  return protectedPatch(
    "/session/lesson/" + args.session_id,
    {
      platform: args.platform,
      platform_change_reason: args.platform_change_reason,
    },
    onSuccess,
    onError
  );
}

export function fetchSession(args, onSuccess, onError) {
  return protectedGet("/session/lesson/" + args.id, undefined, onSuccess, onError);
}

export function fetchStudentsPlanCredits(args, onSuccess, onError) {
  fetchStudentsPlan(
    args,
    student => {
      fetchStudentProfile(
        undefined,
        profile => {
          student.credits = profile.credits;
          onSuccess && onSuccess(student);
        },
        onError
      );
    },
    onError
  );
}

export function fetchStudentsPlan(args, onSuccess /*, onError*/) {
  const student = args.user;
  student.plan = null;

  return protectedGet(
    "/plan/current",
    undefined,
    planResp => {
      if (planResp && planResp.plan) {
        student.first_name = student.profile.first_name;
        student.last_name = student.profile.last_name;
        student.plan = transformPlan(planResp.plan);
        if (!student.sessions) {
          student.sessions = student.plan.sessions;
        }
      }

      return onSuccess && onSuccess(student);
    },
    (/*error*/) => {
      return onSuccess && onSuccess(student);
    }
  );
}

export function fetchFeedback(args, onSuccess, onError) {
  const fetchFeedbackPromise = new Promise((resolve, reject) => {
    protectedGet(`/session/lesson/${args.id}/feedback`, undefined, resolve, reject);
  });
  const fetchSessionPromise = new Promise((resolve, reject) => {
    fetchSession(args, resolve, reject);
  });
  const getHomeworkPromise = new Promise((resolve, reject) => {
    getHomework(
      {
        lesson_id: args.id,
      },
      resolve,
      reject
    );
  });
  Promise.all([fetchFeedbackPromise, fetchSessionPromise, getHomeworkPromise])
    .then(([feedbackResp, session, homeworkResp]) => {
      session.feedback = feedbackResp.feedback;
      session.homework = homeworkResp.homework;
      onSuccess(session);
    })
    .catch(onError);
}

export function patchFeedback(args, payload, onSuccess, onError) {
  return protectedPatch(
    "/session/lesson/" + args.id + "/feedback",
    {
      grammar: payload.grammar,
      homework: payload.homework,
      other: payload.other,
      practice: payload.practice,
      summary: payload.summary,
      vocabulary: payload.vocabulary,
    },
    onSuccess,
    onError
  );
}

export function fetchLessonSession(args, onSuccess, onError) {
  return protectedGet("/session/lesson/" + args.id, undefined, onSuccess, onError);
}

export function getWorkspace(args, onSuccess, onError) {
  return protectedGet(
    "/session/lesson/" + args.lesson_id + "/workspace",
    undefined,
    onSuccess,
    onError
  );
}

export function saveWorkspace(args, onSuccess, onError) {
  return protectedPatch(
    "/session/lesson/" + args.lesson_id + "/workspace",
    {
      workspace: args.workspace,
    },
    onSuccess,
    onError
  );
}

export function getHomework(args, onSuccess, onError) {
  return protectedGet(
    "/session/lesson/" + args.lesson_id + "/homework",
    undefined,
    onSuccess,
    onError
  );
}

export function saveHomework(args, onSuccess, onError) {
  return protectedPatch(
    "/session/lesson/" + args.lesson_id + "/homework",
    {
      homework: args.homework,
    },
    onSuccess,
    onError
  );
}

export function getChat(args, onSuccess, onError) {
  return protectedGet(
    "/session/lesson/" + args.lesson_id + "/chat",
    undefined,
    resp => {
      if (!resp || !resp.chat) {
        return (
          onError &&
          onError("Invalid response: shouldn’t be empty and contain an array 'chat'")
        );
      }
      return onSuccess && onSuccess(resp);
    },
    onError
  );
}

export function getChatHistory(args, user, onSuccess, onError) {
  let prevMessageFrom = null;
  const current_user = getSessionUser();
  getChat(
    args,
    resp => {
      const chatMessages = [];
      resp.chat.forEach(message => {
        let name = null;
        if (message.user_id !== prevMessageFrom) {
          if (message.user_id === current_user.id) {
            name = "You";
          } else {
            if (isUserTeacher()) {
              name = getUserFullName(user.data.user);
            } else {
              name = user.data.instructor.first_name;
            }
          }
        }

        prevMessageFrom = message.user_id;

        chatMessages.push({
          text: message.text,
          user_id: message.user_id,
          userName: name,
          role: message.user_id === current_user.id ? "instructor" : "student",
        });
      });
      return onSuccess && onSuccess({ chat: chatMessages });
    },
    (/*error*/) => {
      // TODO show to user or initialising an empty chatMessages list is ok?
      return onError;
    }
  );
}

export function saveChatMessage(args, onSuccess, onError) {
  return protectedPatch(
    "/session/lesson/" + args.lesson_id + "/chat",
    {
      text: args.text,
      timestamp: moment(getCurrentServerTime()).toISOString(),
    },
    onSuccess,
    onError
  );
}

export function isAssessmentSession(session) {
  return session && session.type === ASSESSMENT_SESSION_TYPE;
}

export function isSessionChanged(newSession, oldSession) {
  if (!newSession || !oldSession) {
    return false;
  }
  return (
    (newSession.name && newSession.name !== oldSession.name) ||
    (newSession.description && newSession.description !== oldSession.description)
  );
}

export function reschedule(args, onSuccess, onError) {
  return protectedPost(
    "/session/lesson/" + args.oldSession.id + "/reschedule",
    {
      scheduled: args.newSession.scheduled,
    },
    resp => {
      if (resp && resp.id && isSessionChanged(args.newSession, args.oldSession)) {
        return updateSessionByParticipant(
          {
            session_id: resp.id, // id of new created session
            session: {
              name: args.newSession.name,
              description: args.newSession.description,
            },
          },
          onSuccess,
          onError
        );
      } else {
        return onSuccess();
      }
    },
    onError
  );
}

export function getEvents(args, onSuccess, onError) {
  return protectedGet(
    "/session/lesson/" + args.lessonId + "/events",
    null,
    resp => {
      return onSuccess(resp.list, resp.session.status);
    },
    onError
  );
}

export function saveEvent(args, onSuccess, onError) {
  return protectedPost(
    "/session/lesson/" + args.lessonId + "/events",
    {
      type: args.eventType,
      platform: args.plaftorm,
    },
    onSuccess,
    onError
  );
}

export function cancelSessionByCS(args, onSuccess, onError) {
  // only for Customer Support
  return protectedPatch(
    "/session/lesson/" + args.session_id,
    {
      status: "instructor-canceled",
      cancellation_reason: args.reason,
    },
    onSuccess,
    onError
  );
}

export function saveTranscription(args) {
  return new Promise((resolve, reject) => {
    return protectedPost(
      `/session/lesson/${args.session_id}/transcription`,
      {
        transcription: args.text,
      },
      resolve,
      reject
    );
  });
}
